import type { ReactNode } from 'react';
import {
	CalendarCheck,
	Check,
	Ear,
	HeartHandshake,
	House,
	Sparkles,
	Sprout,
	Target,
	Users,
	type LucideIcon,
} from 'lucide-react';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	APPROACHES,
	FOCUS_TOPICS,
	HIGHLIGHT_TAGS,
	MAX_FOCUS_TOPICS,
	MAX_SECONDARY_APPROACHES,
	getApproach,
	type HighlightTagMeta,
} from '@/constants/professionalTags';
import { cn } from '@/lib/utils';

export interface ProfessionalPublicTagsValue {
	highlight_tag: Professionals.HighlightTag | null;
	main_approach: Professionals.Approach | null;
	secondary_approaches: Professionals.Approach[];
	focus_topics: Professionals.FocusTopic[];
}

export const EMPTY_PUBLIC_TAGS: ProfessionalPublicTagsValue = {
	highlight_tag: null,
	main_approach: null,
	secondary_approaches: [],
	focus_topics: [],
};

/** Normaliza o que vem do backend (campos opcionais) para o shape do editor. */
export const toPublicTagsValue = (
	source?: Partial<ProfessionalPublicTagsValue> | null,
): ProfessionalPublicTagsValue => ({
	highlight_tag: source?.highlight_tag ?? null,
	main_approach: source?.main_approach ?? null,
	secondary_approaches: [...(source?.secondary_approaches ?? [])],
	focus_topics: [...(source?.focus_topics ?? [])],
});

/** Compara dois valores do editor (ordem das listas não importa). */
export const isSamePublicTags = (
	a: ProfessionalPublicTagsValue,
	b: ProfessionalPublicTagsValue,
) => {
	const sameList = (x: string[], y: string[]) =>
		x.length === y.length && [...x].sort().join() === [...y].sort().join();
	return (
		a.highlight_tag === b.highlight_tag &&
		a.main_approach === b.main_approach &&
		sameList(a.secondary_approaches, b.secondary_approaches) &&
		sameList(a.focus_topics, b.focus_topics)
	);
};

const HIGHLIGHT_ICONS: Record<HighlightTagMeta['icon'], LucideIcon> = {
	sparkles: Sparkles,
	'calendar-check': CalendarCheck,
	target: Target,
	'heart-handshake': HeartHandshake,
	ear: Ear,
	users: Users,
	home: House,
	sprout: Sprout,
};

/** O Select não aceita valor vazio como item; usamos sentinela para "Nenhuma". */
const APPROACH_NONE = '__approach_none__';

// Mesmas convenções visuais do formulário de perfil do profissional.
const labelStyle =
	'flex items-center gap-2 text-[9px] font-black text-brand-purple uppercase tracking-widest ml-1 leading-none mb-1.5';
const inputStyle =
	'w-full bg-stone-50/50 border border-stone-100 rounded-xl px-4 py-2.5 text-stone-700 text-sm font-bold outline-none focus:bg-white focus:border-stone-300 transition-all';
const errorStyle = 'text-red-500 text-[10px] font-bold ml-1 mt-1';
const helperStyle = 'text-[11px] text-stone-400 ml-1 mb-2';

const chipBase =
	'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-all select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/40';
const chipIdle =
	'border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:bg-stone-50';
const chipActive = 'border-brand-purple bg-brand-purple text-white shadow-sm';
const chipDisabled = 'cursor-not-allowed opacity-40';

const Field = ({
	label,
	helper,
	error,
	children,
	trailing,
}: {
	label: string;
	helper?: string;
	error?: string;
	children: ReactNode;
	trailing?: ReactNode;
}) => (
	<div className="text-left">
		<div className="flex items-start justify-between gap-2">
			<label className={labelStyle}>{label}</label>
			{trailing}
		</div>
		{helper && <p className={helperStyle}>{helper}</p>}
		{children}
		{error && <p className={errorStyle}>{error}</p>}
	</div>
);

interface ProfessionalPublicTagsFieldsProps {
	value: ProfessionalPublicTagsValue;
	onChange: (next: ProfessionalPublicTagsValue) => void;
	/** Mostra o asterisco e a dica de obrigatoriedade na abordagem principal. */
	requireMainApproach?: boolean;
	errors?: Partial<Record<keyof ProfessionalPublicTagsValue, string>>;
	/** Layout mais denso (chips menores, grade mais compacta). */
	compact?: boolean;
	disabled?: boolean;
}

const ProfessionalPublicTagsFields = ({
	value,
	onChange,
	requireMainApproach = false,
	errors,
	compact = false,
	disabled = false,
}: ProfessionalPublicTagsFieldsProps) => {
	const update = (patch: Partial<ProfessionalPublicTagsValue>) =>
		onChange({ ...value, ...patch });

	const toggleInList = <T extends string>(
		list: T[],
		item: T,
		max: number,
	): T[] | null => {
		if (list.includes(item)) return list.filter((i) => i !== item);
		if (list.length >= max) return null;
		return [...list, item];
	};

	const selectMainApproach = (raw: string) => {
		const next =
			raw === APPROACH_NONE ? null : (raw as Professionals.Approach);
		update({
			main_approach: next,
			// Sem principal não há complementares; e a principal não repete nelas.
			secondary_approaches: next
				? value.secondary_approaches.filter((a) => a !== next)
				: [],
		});
	};

	const mainApproachMeta = getApproach(value.main_approach);

	return (
		<div className={cn('space-y-6', compact && 'space-y-4')}>
			{/* Tag de destaque */}
			<Field
				label="Tag de destaque"
				helper="Escolha uma característica para aparecer em destaque no seu card."
				error={errors?.highlight_tag}
			>
				<div
					className={cn(
						'grid gap-2',
						compact
							? 'grid-cols-2 md:grid-cols-3'
							: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
					)}
					role="radiogroup"
					aria-label="Tag de destaque"
				>
					<button
						type="button"
						role="radio"
						aria-checked={value.highlight_tag === null}
						disabled={disabled}
						onClick={() => update({ highlight_tag: null })}
						className={cn(
							'flex items-center gap-2 rounded-xl border px-3 py-2 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/40',
							value.highlight_tag === null
								? 'border-stone-400 bg-stone-100'
								: 'border-dashed border-stone-200 bg-white hover:bg-stone-50',
							disabled && chipDisabled,
						)}
					>
						<span className="text-xs font-bold text-stone-500">
							Nenhuma
						</span>
					</button>

					{HIGHLIGHT_TAGS.map((tag) => {
						const Icon = HIGHLIGHT_ICONS[tag.icon];
						const active = value.highlight_tag === tag.key;
						return (
							<button
								key={tag.key}
								type="button"
								role="radio"
								aria-checked={active}
								disabled={disabled}
								title={tag.hint}
								onClick={() =>
									update({
										highlight_tag: active ? null : tag.key,
									})
								}
								className={cn(
									'flex items-start gap-2 rounded-xl border px-3 py-2 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/40',
									active
										? 'border-brand-purple ring-2 ring-brand-purple/30'
										: 'border-stone-100 hover:border-stone-300',
									tag.chipClassName,
									disabled && chipDisabled,
								)}
							>
								<Icon
									size={compact ? 14 : 16}
									className="mt-0.5 shrink-0"
								/>
								<span className="min-w-0 flex-1">
									<span className="block text-xs font-black leading-tight">
										{tag.label}
									</span>
									{!compact && (
										<span className="mt-0.5 block text-[10px] font-medium leading-snug opacity-80">
											{tag.hint}
										</span>
									)}
								</span>
								<Check
									size={14}
									strokeWidth={3}
									className={cn(
										'shrink-0',
										!active && 'hidden',
									)}
								/>
							</button>
						);
					})}
				</div>
			</Field>

			{/* Abordagem principal */}
			<Field
				label={`Abordagem principal${requireMainApproach ? ' *' : ''}`}
				helper={
					requireMainApproach
						? 'Obrigatória para psicólogos. É a primeira coisa que o paciente vê.'
						: 'Linha teórica que orienta o seu trabalho (opcional).'
				}
				error={errors?.main_approach}
			>
				<Select
					value={value.main_approach ?? APPROACH_NONE}
					onValueChange={selectMainApproach}
					disabled={disabled}
				>
					<SelectTrigger className={`${inputStyle} h-auto`}>
						<SelectValue>
							{mainApproachMeta ? (
								<span className="text-stone-700">
									{mainApproachMeta.label}
								</span>
							) : (
								<span className="font-normal text-stone-400">
									Escolher abordagem
								</span>
							)}
						</SelectValue>
					</SelectTrigger>
					<SelectContent>
						<SelectGroup>
							<SelectItem value={APPROACH_NONE}>
								<span className="text-stone-400">Nenhuma</span>
							</SelectItem>
							{APPROACHES.map((approach) => (
								<SelectItem
									key={approach.key}
									value={approach.key}
								>
									{approach.label}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>
				{mainApproachMeta && (
					<p className="ml-1 mt-1.5 text-[11px] text-stone-400">
						{mainApproachMeta.hint}
					</p>
				)}
			</Field>

			{/* Abordagens complementares */}
			<Field
				label="Abordagens complementares"
				helper={
					value.main_approach
						? `Até ${MAX_SECONDARY_APPROACHES} abordagens que você também utiliza.`
						: 'Escolha a abordagem principal para liberar as complementares.'
				}
				error={errors?.secondary_approaches}
				trailing={
					<span className="text-[10px] font-bold tabular-nums text-stone-400">
						{value.secondary_approaches.length}/
						{MAX_SECONDARY_APPROACHES}
					</span>
				}
			>
				<div className="flex flex-wrap gap-2">
					{APPROACHES.map((approach) => {
						const active = value.secondary_approaches.includes(
							approach.key,
						);
						const isMain = value.main_approach === approach.key;
						const full =
							value.secondary_approaches.length >=
							MAX_SECONDARY_APPROACHES;
						const noMain = !value.main_approach;
						const blocked =
							disabled || noMain || isMain || (!active && full);
						return (
							<button
								key={approach.key}
								type="button"
								aria-pressed={active}
								disabled={blocked}
								title={
									noMain
										? 'Escolha a abordagem principal primeiro'
										: isMain
										  ? 'Já é a sua abordagem principal'
										  : approach.hint
								}
								onClick={() => {
									const next = toggleInList(
										value.secondary_approaches,
										approach.key,
										MAX_SECONDARY_APPROACHES,
									);
									if (next)
										update({ secondary_approaches: next });
								}}
								className={cn(
									chipBase,
									active ? chipActive : chipIdle,
									blocked && chipDisabled,
									compact && 'px-2.5 py-1 text-[11px]',
								)}
							>
								<Check
									size={12}
									strokeWidth={3}
									className={active ? '' : 'hidden'}
								/>
								<span>{approach.short}</span>
							</button>
						);
					})}
				</div>
			</Field>

			{/* Temas de atuação */}
			<Field
				label="Temas de atuação"
				helper={`Até ${MAX_FOCUS_TOPICS} temas com que você mais trabalha.`}
				error={errors?.focus_topics}
				trailing={
					<span className="text-[10px] font-bold tabular-nums text-stone-400">
						{value.focus_topics.length}/{MAX_FOCUS_TOPICS}
					</span>
				}
			>
				<div className="flex flex-wrap gap-2">
					{FOCUS_TOPICS.map((topic) => {
						const active = value.focus_topics.includes(topic.key);
						const full =
							value.focus_topics.length >= MAX_FOCUS_TOPICS;
						const blocked = disabled || (!active && full);
						return (
							<button
								key={topic.key}
								type="button"
								aria-pressed={active}
								disabled={blocked}
								onClick={() => {
									const next = toggleInList(
										value.focus_topics,
										topic.key,
										MAX_FOCUS_TOPICS,
									);
									if (next) update({ focus_topics: next });
								}}
								className={cn(
									chipBase,
									active ? chipActive : chipIdle,
									blocked && chipDisabled,
									compact && 'px-2.5 py-1 text-[11px]',
								)}
							>
								<Check
									size={12}
									strokeWidth={3}
									className={active ? '' : 'hidden'}
								/>
								<span>{topic.label}</span>
							</button>
						);
					})}
				</div>
			</Field>
		</div>
	);
};

export { ProfessionalPublicTagsFields };
